import { redactCredentialsInText } from "../security/redact-url";

const REPLAY_SNAPSHOT_EVENT = "$snapshot";

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

type Redacted = WeakMap<object, unknown>;

function redactArray(values: unknown[], redacted: Redacted): unknown[] {
  let changed = false;
  const next = values.map((value) => {
    const redactedValue = redactValue(value, redacted);
    if (redactedValue !== value) changed = true;
    return redactedValue;
  });
  return changed ? next : values;
}

function redactEntries(
  record: Record<string, unknown>,
  redacted: Redacted,
): Record<string, unknown> {
  let changed = false;
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    const redactedKey = redactCredentialsInText(key);
    const redactedValue = redactValue(value, redacted);
    if (redactedKey !== key || redactedValue !== value) changed = true;
    next[redactedKey] = redactedValue;
  }
  return changed ? next : record;
}

function redactValue(value: unknown, redacted: Redacted): unknown {
  if (typeof value === "string") return redactCredentialsInText(value);
  if (typeof value !== "object" || value === null) return value;
  if (redacted.has(value)) return redacted.get(value);
  if (!Array.isArray(value) && !isPlainRecord(value)) return value;
  redacted.set(value, value);
  const result = Array.isArray(value)
    ? redactArray(value, redacted)
    : redactEntries(value, redacted);
  redacted.set(value, result);
  return result;
}

export function redactBearerCredentials(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return redactEntries(properties, new WeakMap());
}

type AnalyticsEvent = {
  event?: string;
  properties?: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
};

export function redactBearerEvent<Event extends AnalyticsEvent | null>(
  event: Event,
): Event {
  if (!event || event.event === REPLAY_SNAPSHOT_EVENT) return event;
  return {
    ...event,
    ...(event.properties && { properties: redactBearerCredentials(event.properties) }),
    ...(event.$set && { $set: redactBearerCredentials(event.$set) }),
    ...(event.$set_once && { $set_once: redactBearerCredentials(event.$set_once) }),
  };
}
